import { useState, useEffect, useRef, useCallback } from "react";
import webrtcService from "../services/webrtcService";

export const useWebRTC = () => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [connectionState, setConnectionState] = useState("new");
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCallEnd, setIsCallEnd] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  // Initialize WebRTC service
  useEffect(() => {
    const initializeWebRTC = async () => {
      try {
        const success = await webrtcService.initialize();
        if (success) {
          setIsInitialized(true);

          // Set up callbacks
          webrtcService.setCallbacks({
            onLocalStream: (stream) => {
              console.log("Local stream received");
              setLocalStream(stream);
              if (localVideoRef.current) {
                localVideoRef.current.srcObject = stream;
              }
            },
            onRemoteStream: (stream) => {
              console.log("Remote stream received");
              setRemoteStream(stream);
              if (remoteVideoRef.current) {
                remoteVideoRef.current.srcObject = stream;
              }
            },
            onConnectionStateChange: (state) => {
              console.log("Connection state changed:", state);
              setConnectionState(state);
              setIsCallActive(state === "connected");
            },
            onCallStarted: () => {
              console.log("Call started");
              setIsCallActive(true);
            },
            onCallEnded: () => {
              console.log("Call ended");
              setIsCallActive(false);
              setLocalStream(null);
              setRemoteStream(null);
              setIsCallEnd(true);
              if (localVideoRef.current) {
                localVideoRef.current.srcObject = null;
              }
              if (remoteVideoRef.current) {
                remoteVideoRef.current.srcObject = null;
              }
            },
            onError: (errorMessage) => {
              console.error("WebRTC error:", errorMessage);
              setError(errorMessage);
            },
          });
        }
      } catch (error) {
        console.error("Failed to initialize WebRTC:", error);
        setError("Failed to initialize WebRTC: " + error.message);
      }
    };

    initializeWebRTC();

    // Cleanup on unmount
    return () => {
      if (isCallActive) {
        webrtcService.endCall();
      }
    };
  }, []);

  // Update video element when streams change
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Start a call (create offer)
  const startCall = useCallback(async () => {
    try {
      setError(null);
      await webrtcService.createOffer();
    } catch (error) {
      setError("Failed to start call: " + error.message);
    }
  }, []);

  // End call
  const endCall = useCallback(() => {
    try {
      webrtcService.endCall();
    } catch (error) {
      setError("Failed to end call: " + error.message);
    }
  }, []);

  // Toggle video
  const toggleVideo = useCallback(() => {
    try {
      const enabled = webrtcService.toggleVideo();
      setIsVideoEnabled(enabled);
      return enabled;
    } catch (error) {
      setError("Failed to toggle video: " + error.message);
      return isVideoEnabled;
    }
  }, [isVideoEnabled]);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    try {
      const enabled = webrtcService.toggleAudio();
      setIsAudioEnabled(enabled);
      return enabled;
    } catch (error) {
      setError("Failed to toggle audio: " + error.message);
      return isAudioEnabled;
    }
  }, [isAudioEnabled]);

  // Get user media manually
  const getUserMedia = useCallback(
    async (constraints = { video: false, audio: true }) => {
      try {
        setError(null);
        const stream = await webrtcService.getUserMedia(constraints);
        return stream;
      } catch (error) {
        setError("Failed to get user media: " + error.message);
        throw error;
      }
    },
    []
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Get call statistics
  const getStats = useCallback(async () => {
    try {
      return await webrtcService.getStats();
    } catch (error) {
      setError("Failed to get stats: " + error.message);
      return null;
    }
  }, []);

  return {
    // State
    isCallActive,
    connectionState,
    localStream,
    remoteStream,
    isVideoEnabled,
    isAudioEnabled,
    error,
    isInitialized,
    isCallEnd,

    // Refs for video elements
    localVideoRef,
    remoteVideoRef,

    // Actions
    startCall,
    endCall,
    toggleVideo,
    toggleAudio,
    getUserMedia,
    clearError,
    getStats,

    // Service reference for advanced usage
    webrtcService,
  };
};
